import React from 'react';
import { MapPin, MessageCircle } from 'lucide-react';
import { Escort } from '../types';
import { getWhatsAppUrl } from "../utils/whatsappUtils.ts";
import { capitalizeFirstLetter } from '../utils/textUtils';
import { useTranslation } from '../hooks/useTranslation';
import { useDisclaimer } from '../contexts/DisclaimerContext';
import useMobile from '../hooks/useMobile';
import { getVisitorId } from '../utils/visitorId';
import VipBadge from './atoms/VipBadge';
import VetrinaBadge from './atoms/VetrinaBadge';

const isVideoUrl = (url: string) => /\.(mp4)(\?.*)?$/i.test(url);

interface CardProps {
  escort: Escort;
  onFavorite?: (id: string) => void;
  onViewProfile: (id: string) => void;
  priority?: boolean;
  source?: string;
  mostVisited?: boolean;
  mostContacted?: boolean;
  mostReviewed?: boolean;
}

const Card: React.FC<CardProps> = ({ escort, onFavorite, onViewProfile, priority = false, source = 'card', mostVisited = false, mostContacted = false, mostReviewed = false }) => {
  const isMobile = useMobile();
  const { t, currentLanguage } = useTranslation();
  const { hasAcceptedDisclaimer } = useDisclaimer();

  const getBadgeImage = () => {
    const lang = currentLanguage?.split(/[-_]/)[0]?.toLowerCase() || 'it';
    if (mostVisited) return `/${lang}_visited.png`;
    if (mostContacted) return `/${lang}_contacted.png`;
    if (mostReviewed) return `/${lang}_reviewed.png`;
    return null;
  };

  const badgeImage = getBadgeImage();

  const handleWhatsAppClick = () => {
    if (escort.whatsappNumber) {
      const vid = getVisitorId() ? `&vid=${encodeURIComponent(getVisitorId()!)}` : '';
      const url = `https://img.escortfun.net/cache/contact.php?i=${encodeURIComponent(String(escort.id))}&t=wa&s=${encodeURIComponent(source)}${vid}`;
      try { navigator.sendBeacon(url); } catch (_) { fetch(url).catch(() => {}); }
      window.open(getWhatsAppUrl(escort.whatsappNumber), '_blank');
    }
  };

  // Estrai la città dal campo location (formato: "Città / Provincia")
  const getCity = (location: string) => {
    return location.split(' / ')[0];
  };

  return (
    <div
      className={`rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300 bg-white relative cursor-pointer group ${escort.featured ? 'border-2 border-fuchsia-400' : escort.isVetrina ? 'border-2 border-yellow-300' : ''}`}
      onClick={() => onViewProfile(escort.id)}
    >
      <div className="relative" style={{ height: '320px', overflow: 'hidden', contain: 'layout' }}>
        {hasAcceptedDisclaimer ? (
          isVideoUrl(escort.imageUrl) ? (
            <video
              src={escort.imageUrl}
              muted
              autoPlay
              loop
              playsInline
              className="w-full h-full object-cover"
              style={{ aspectRatio: '1 / 1', display: 'block' }}
            />
          ) : (
            <img 
              src={escort.imageUrl} 
              alt={escort.name} 
              className="w-full h-full object-cover"
              loading={priority ? "eager" : "lazy"}
              width="320"
              height="320"
              style={{ aspectRatio: '1 / 1', display: 'block' }}
            />
          )
        ) : (
          <div 
            className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center"
            style={{ aspectRatio: '1 / 1' }}
          >
            <div className="text-gray-400 text-center px-4">
              <div className="text-4xl mb-2">🔒</div>
            </div>
          </div>
        )}
        
        {/* Watermark */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div 
            className="text-white/20 text-2xl font-bold transform rotate-12 select-none"
            style={{ minWidth: '200px', minHeight: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            {t('common.escortfun_net')}
          </div>
        </div>
        
        {/* WhatsApp Icon */}
        {badgeImage && (
          <img
            src={badgeImage}
            alt="Badge"
            className="absolute bottom-2 right-1 w-24 h-16 z-20 object-cover"
          />
        )}
        {escort.whatsappNumber && (
          <button 
            onClick={(e) => {
              e.stopPropagation();
              handleWhatsAppClick();
            }}
            className="absolute top-1 left-1 bg-green-500 py-1 px-1 rounded-full shadow-lg hover:bg-green-600 transition-colors duration-200 z-10"
            aria-label="Contatta su WhatsApp"
            style={{ minWidth: '24px', minHeight: '24px' }}
          >
            <MessageCircle className="h-5 w-5 text-white" />
          </button>
        )}

        {/* VIP Badge */}
        {escort.featured && (
          <div className="absolute top-1 right-1 z-10" style={{ minWidth: '60px', minHeight: '24px' }}>
            <VipBadge label={t('common.vipBadge')} />
          </div>
        )}
        
        {/* Vetrina Badge */}
        {!escort.featured && escort.isVetrina && (
          <div className="absolute top-1 right-1 z-10" style={{ minWidth: '80px', minHeight: '24px' }}>
            <VetrinaBadge label={t('card.showcaseBadge')} />
          </div>
        )}
        
        {/* Verified Badge */}
        {escort.isVerified && (
          <div className="absolute bottom-1 left-1 flex items-center z-10" style={{ minHeight: '24px' }}>
            <div className="flex items-center backdrop-blur-sm text-white text-xs py-1 rounded-full shadow-lg">
              {/* Twitter/X-style verified checkmark */}
              <svg viewBox="0 0 24 24" className="h-4 w-4 flex-shrink-0" aria-label="Verified">
                <circle cx="12" cy="12" r="12" fill="#1D9BF0" />
                <path
                  d="M9.5 16.5L5.5 12.5L6.91 11.09L9.5 13.67L17.09 6.08L18.5 7.5L9.5 16.5Z"
                  fill="white"
                />
              </svg>
            </div>
          </div>
        )}
      </div>


      {isMobile ? (
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between px-2 py-2 md:p-4">
          
          <div className="flex items-center w-full md:w-auto md:flex-1">
            <div className="flex items-center min-w-0 flex-1">
              <span className="text-base font-semibold text-gray-800 truncate mr-2">{escort.name}</span>
              {escort.category && (
                <div className="bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 text-xs px-3 py-1 rounded-full border border-purple-200 whitespace-nowrap flex-shrink-0">
                  {capitalizeFirstLetter(escort.category)}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center text-gray-600 mt-1 md:mt-0 min-w-0" style={{ maxWidth: '120px' }}>
            <MapPin className="h-4 w-4 mr-1 text-pink-500 flex-shrink-0" />
            <span className="text-sm truncate">{getCity(escort.location)}</span>
          </div>

        </div>
      ) : (
        <div className="p-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-1" style={{ minHeight: '28px' }}>
            {escort.name}
          </h3>
          <div className="flex items-center justify-between mb-2" style={{ minHeight: '32px' }}>
            <div className="flex items-center text-gray-600">
              <MapPin className="h-4 w-4 mr-1 text-pink-500" />
              <span className="text-sm">{escort.location}</span>
            </div>
            {escort.category && (
              <div className="bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 text-xs px-3 py-1 rounded-full border border-purple-200 text-center" style={{ minWidth: '80px', minHeight: '24px' }}>
                {capitalizeFirstLetter(escort.category)}
              </div>
            )}
          </div>
        </div>
      )}
      
    </div>
  );
};

export default Card;